import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import useIsMobile from "../../../hooks/useIsMobile.js";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import AgGridActionMenu from "../../../components/AgGridAction/AgGridAction";
import TitleCard from "../../../components/TitleCard/TitleCard";
import Loader from "../../../components/Loader/Loader";
import ReusableModal from "../../../components/Modal/ReusableModal";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import ParkingRegisterForm from "./ParkingRegisterForm";
import { Trash2, SquarePen, LogOut } from "lucide-react";
import { formatDateYYYYMMDD, formatTime24h } from '../../../utils/dateFormat.js';
import { cleanBackendErrorMessage } from '../../../utils/plateValidation';
import MobileCardList from "../../../components/MobileCardList/MobileCardList.jsx";
import MiniFormRegister from "./MiniFormRegister.jsx";
import ToggleTabs from "../../../components/ToggleTabs/ToggleTabs";
import FilterActions from "../../../components/FilterActions/FilterActions";
import CancelButton from "../../../components/CancelButton/CancelButton";

const ParkingRegister = () => {
  const gridApiRef = useRef(null);

  // UI STATE
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedParking, setSelectedParking] = useState(null);
  const [activeTab, setActiveTab] = useState("registro");
  const [filterColumn, setFilterColumn] = useState("placa");
  const [filterValue, setFilterValue] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const activeFilterRef = useRef(null);

  // MOBILE DATA
  const [mobileData, setMobileData] = useState([]);
  const [mobilePage, setMobilePage] = useState(0);
  const [mobileTotal, setMobileTotal] = useState(0);

  // MOBILE DETECTION
  const isMobile = useIsMobile();

  const loadMobileData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/parkingregister/${activeFilter ? "search" : "get-all"}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: mobilePage,
            size: pageSize,
            ...(activeFilter || {}),
          },
        }
      );

      const formattedData = response.data.data.map((item) => ({
        fecha_entrada: formatDateYYYYMMDD(item.entryTime),
        hora_entrada: formatTime24h(item.entryTime),
        fecha_salida: item.exitTime ? formatDateYYYYMMDD(item.exitTime) : "",
        hora_salida: item.exitTime ? formatTime24h(item.exitTime) : "",
        ...item,
      }));

      setMobileData(formattedData);
      setMobileTotal(response.data.total);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, mobilePage, pageSize]);


  useEffect(() => {
    if (isMobile) {
      loadMobileData();
    }
  }, [isMobile, loadMobileData]);

  // CONFIRM MODAL
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: "confirm",
    title: "Eliminar usuario",
    message: "¿Seguro que deseas eliminar este usuario?",
    onConfirm: null,
  });

  const [exportModal, setExportModal] = useState({
    isOpen: false,
    fecha_inicial: "",
    fecha_final: "",
  });

  // AG GRID CONFIG
  const components = useMemo(
    () => ({
      actionMenu: AgGridActionMenu,
    }),
    []
  );

  const [colDefs] = useState([
    { field: "placa", headerName: "Placa", maxWidth: 100, valueFormatter: (params) => params.value?.toString().toUpperCase() || "" },
    { field: "type_vehicle", headerName: "Tipo Vehículo", maxWidth: 130, valueFormatter: (params) => params.value?.toString().toUpperCase() || "", },
    { field: "fecha_entrada", headerName: "Fecha Entrada" },
    { field: "hora_entrada", headerName: "Hora Entrada" },
    { field: "numberOfHelmets", headerName: "Cascos", maxWidth: 90 },
    { field: "fecha_salida", headerName: "Fecha Salida" },
    { field: "hora_salida", headerName: "Hora Salida" },
    { field: "status", headerName: "Estado", maxWidth: 100, valueFormatter: (params) => params.value === "in" ? "Parqueado" : "Retirado"},
    {
      headerName: "Opciones",
      cellRenderer: "actionMenu",
      minWidth: 90,
      maxWidth: 100,
      sortable: true,
      filter: false,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      cellRendererParams: {
        onEdit: (row) => handleEditParking(row),
        onDelete: (row) => handleDeleteParking(row),
        onFinalize: (row) => handleFinalizeParking(row),
      },
    },
  ]);

  const defaultColDef = useMemo(
    () => ({ 
      flex: 1, editable: false, filter: false, sortable: true, resizable: true, minWidth: 100,})
    ,[]
  );

  // DATASOURCE (DESKTOP)
  const createDataSource = useCallback(() => {
    return {
      getRows: async (params) => {
        setLoading(true);
        try {
          const size = params.endRow - params.startRow;
          const page = size > 0 ? params.startRow / size : 0;

          const sortModel = params.sortModel || [];

          const token = localStorage.getItem("access_token");
          const currentFilter = activeFilterRef.current;
          const searchParams = {
            page,
            size,
            ...(currentFilter || {}),
          };

          if (Array.isArray(sortModel) && sortModel.length > 0) {
            const [{ colId, sort }] = sortModel;
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/parkingregister/search`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: { ...searchParams, orderBy: colId, order: sort },
              }
            );

            const newData = response.data.data.map((item) => ({
              fecha_entrada: formatDateYYYYMMDD(item.entryTime),
              hora_entrada: formatTime24h(item.entryTime),
              fecha_salida: item.exitTime ? formatDateYYYYMMDD(item.exitTime) : '--',
              hora_salida: item.exitTime ? formatTime24h(item.exitTime) : '--',
              status: item.status === "in" ? "Parqueado" : "Retirado",
              ...item,
            }));

            params.successCallback(newData, response.data.total);
            setLoading(false);
            return;
          }

          if (currentFilter) {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/parkingregister/search`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: searchParams,
              }
            );

            const newData = response.data.data.map((item) => ({
              fecha_entrada: formatDateYYYYMMDD(item.entryTime),
              hora_entrada: formatTime24h(item.entryTime),
              fecha_salida: item.exitTime ? formatDateYYYYMMDD(item.exitTime) : '--',
              hora_salida: item.exitTime ? formatTime24h(item.exitTime) : '--',
              status: item.status === "in" ? "Parqueado" : "Retirado",
              ...item,
            }));

            params.successCallback(newData, response.data.total);
            setLoading(false);
            return;
          }

          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/parkingregister/get-all`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { page, size },
            }
          );

          const newData = response.data.data.map((item) => ({
            fecha_entrada: formatDateYYYYMMDD(item.entryTime),
            hora_entrada: formatTime24h(item.entryTime),
            fecha_salida: item.exitTime ? formatDateYYYYMMDD(item.exitTime) : '--',
            hora_salida: item.exitTime ? formatTime24h(item.exitTime) : '--',
            status: item.status === "in" ? "Parqueado" : "Retirado",
            ...item,
          }));

          // console.log('-nuevos datos::::::::::',newData);
          params.successCallback(newData, response.data.total);
        } catch (error) {
          console.error(error);
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    };
  }, []);

  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;

      if (typeof params.api.setDatasource === "function") {
        params.api.setDatasource(createDataSource());
      } else {
        params.api.setGridOption("datasource", createDataSource());
      }
    },
    [createDataSource]
  );

  const onPaginationChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const api = gridApiRef.current;
    const newPageSize = api.paginationGetPageSize();
    // setPageSize(gridApiRef.current.paginationGetPageSize());
    setPageSize((prev) => (prev !== newPageSize ? newPageSize : prev));
  }, []);


  const handleEditParking = (params) => {
    const data = params?.rowData || params;
    setSelectedParking(data);
    setShowEditModal(true);
  };

  const handleFinalizeParking = (params) => {
    const data = params?.data || params;

    setConfirmModal({
      isOpen: true,
      type: "confirm",
      title: "Finalizar registro",
      message: "¿Deseas finalizar este registro de parqueo?",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("access_token");
          await axios.put(
            `${process.env.REACT_APP_API_URL}/parkingregister/update/${data._id}`,
            {
              exitTime: new Date().toISOString(),
              status: "out",
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          setConfirmModal({
            isOpen: true,
            type: "success",
            title: "Registro finalizado",
            message: "El registro fue finalizado correctamente.",
            onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
          });

          if (isMobile) {
            await loadMobileData();
          }

          gridApiRef.current?.purgeInfiniteCache();
        } catch (error) {
          console.error(error);
          setConfirmModal({
            isOpen: true,
            type: "error",
            title: "Error",
            message: "No se pudo finalizar el registro.",
            onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
          });
        }
      },
    });
  };

  const handleDeleteParking = (params) => {
    const data = params?.data || params;

    setConfirmModal({
      isOpen: true,
      type: "confirm",
      title: "Eliminar registro",
      message: "¿Deseas eliminar este registro de parqueo?",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("access_token");
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/parkingregister/delete/${data._id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          setConfirmModal({
            isOpen: true,
            type: "success",
            title: "Registro eliminado",
            message: "El registro fue eliminado correctamente.",
            onConfirm: () =>
              setConfirmModal((p) => ({ ...p, isOpen: false })),
          });
          if (isMobile) {
            setMobilePage(0); // opcional: volver a la primera página           
            await loadMobileData(); // Recarga datos móviles si es necesario
          }
          gridApiRef.current?.purgeInfiniteCache();
        } catch {
          setConfirmModal({
            isOpen: true,
            type: "error",
            title: "Error",
            message: "No se pudo eliminar el registro.",
            onConfirm: () =>
              setConfirmModal((p) => ({ ...p, isOpen: false })),
          });
        }
      },
    });
  };

  const handleFilterChange = ({ target }) => {
    const value = target.value.trim();
    const nextFilter = value
      ? { column: target.column, value }
      : null;

    activeFilterRef.current = nextFilter;
    setActiveFilter(nextFilter);
    setIsFilterActive(Boolean(nextFilter));
    setMobilePage(0);
    gridApiRef.current?.purgeInfiniteCache();
  };

  const resetFilterFormState = () => {
    setIsFilterActive(false);
  };

  const openExportModal = () => {
    setExportModal({
      isOpen: true,
      fecha_inicial: "",
      fecha_final: "",
    });
  };

  const handleExportParkingExcel = async () => {
    const { fecha_inicial, fecha_final } = exportModal;

    if (!fecha_inicial || !fecha_final) {
      setConfirmModal({
        isOpen: true,
        type: "error",
        title: "Faltan fechas",
        message: "Debes seleccionar la fecha inicial y final antes de exportar.",
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
      });
      return;
    }

    if (new Date(fecha_inicial) > new Date(fecha_final)) {
      setConfirmModal({
        isOpen: true,
        type: "error",
        title: "Fechas inválidas",
        message: "La fecha inicial no puede ser mayor que la fecha final.",
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
      });
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/parkingRegister/export-excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fecha_inicial,
            fecha_final,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "parking_register.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportModal({ isOpen: false, fecha_inicial: "", fecha_final: "" });
    } catch (error) {
      console.error("Error exportando Excel:", error);
      setConfirmModal({
        isOpen: true,
        type: "error",
        title: "Error al exportar",
        message: "No se pudo exportar el archivo Excel.",
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
      });
    }
  };

  // RENDER
  return (
    <>
      <TitleCard title="Registro de Parqueo" />

      <ToggleTabs
        tabs={[
          {
            id: "registro",
            label: "Registro",
            activeClassName: "border-blue-200 bg-blue-600 text-white shadow-sm",
            inactiveClassName: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
            ariaLabel: activeTab === "registro" ? "Ocultar formulario" : "Mostrar formulario",
            title: activeTab === "registro" ? "Ocultar formulario" : "Mostrar formulario",
          },
          {
            id: "acciones",
            label: "Acciones",
            activeClassName: "border-blue-200 bg-blue-600 text-white shadow-sm",
            inactiveClassName: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
            ariaLabel: activeTab === "acciones" ? "Ocultar acciones de tabla" : "Mostrar acciones de tabla",
            title: activeTab === "acciones" ? "Ocultar acciones de tabla" : "Mostrar acciones de tabla",
          },
        ]}
        onTabChange={setActiveTab}
      />

      <div className="relative w-full mb-4">
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            activeTab === "registro" ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <MiniFormRegister
            isMobile={isMobile}
            gridApiRef={gridApiRef}
            loadMobileData={loadMobileData}
            onSuccess={() => {
              setConfirmModal({
                isOpen: true,
                type: "success",
                title: "Registro Exitoso",
                message: "El vehículo fue registrado correctamente",
                onConfirm: () =>
                  setConfirmModal((p) => ({ ...p, isOpen: false })),
              });
            }}
            onError={(error) => {
              const backendError =
                typeof error === "string"
                  ? error
                  : error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    error?.message ||
                    "Error al crear el registro";

              const errorMessage = cleanBackendErrorMessage(backendError);

              setConfirmModal({
                isOpen: true,
                type: "error",
                title: "Error al crear el registro",
                message: errorMessage || "Error al crear el registro",
                onConfirm: () =>
                  setConfirmModal((p) => ({ ...p, isOpen: false })),
              });
            }}
          />
        </div>
      </div>

      <div className="relative w-full mb-4">
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            activeTab === "acciones" ? "max-h-[260px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <FilterActions
            column={filterColumn}
            value={filterValue}
            isFilterActive={isFilterActive}
            columnOptions={[
              { value: "placa", label: "Placa" },
              { value: "type_vehicle", label: "Tipo vehículo" },
              { value: "status", label: "Estado" },
              { value: "entryTime", label: "Fecha entrada" },
              { value: "exitTime", label: "Fecha salida" },
            ]}
            onColumnChange={(column) => {
              setFilterColumn(column);
              setFilterValue("");
              resetFilterFormState();
            }}
            onValueChange={(value) => {
              setFilterValue(value);
              if (!value) {
                setFilterColumn("");
              }
              resetFilterFormState();
            }}
            onApply={() => {
              setIsFilterActive(true);
              handleFilterChange({ target: { column: filterColumn, value: filterValue } });
            }}
            onClear={() => {
              setFilterColumn("");
              setFilterValue("");
              handleFilterChange({ target: { column: "", value: "" } });
            }}
            onExport={openExportModal}
          />
        </div>
      </div>
      {/* MOBILE */}
      <MobileCardList
        isMobile={isMobile}
        loading={loading}
        loaderText="Cargando registros..."
        data={mobileData}
        mobilePage={mobilePage}
        setMobilePage={setMobilePage}
        totalPages={Math.ceil(mobileTotal / pageSize)}
        totalItems={mobileTotal}
        pageSize={pageSize}
        renderItem={(item) => (
          <div className="flex">
            <div className="flex-1 grid gap-2 [@media(min-width:430px)]:grid-cols-2 [@media(min-width:430px)]:grid-rows-5">
                <div className="font-semibold col-start-1">{(item.placa || "").toString().toUpperCase()}</div>
                <div className="text-sm font-semibold col-start-2">{(item.type_vehicle || "").toString().toUpperCase()}</div>
                <div className="text-sm font-semibold col-start-1 row-start-3">{item.fecha_entrada || "--"}</div>
                <div className="text-sm font-semibold col-start-2 row-start-3"> {item.hora_entrada || "--"}</div>
                <div className="text-sm font-semibold col-start-1 row-start-5">{item.fecha_salida || "--"}</div>
                <div className="text-sm font-semibold col-start-2 row-start-5">{item.hora_salida || "--"}</div>
                <div className="col-start-1 text-gray-600 row-start-2">Entrada:</div>
                <div className="col-start-1 text-gray-600 row-start-4">Salida:</div>
            </div>

            <div className="flex flex-col justify-center items-end space-y-3">
              <button onClick={() => handleEditParking(item)}>
                <SquarePen size={18} color="#16A34A" strokeWidth={1.2} />
              </button>
              <button onClick={() => handleFinalizeParking(item)}>
                <LogOut size={18} color="#2563EB" strokeWidth={1.2} />
              </button>
              <button onClick={() => handleDeleteParking(item)}>
                <Trash2 size={18} color="#B91C1C" strokeWidth={1.2} />
              </button>
            </div>
          </div>
        )}
      />

      {/* DESKTOP */}
      {!isMobile && (
        <div className="relative ag-theme-material my-4" style={{ height: 520 }}>
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10">
              <Loader message="Cargando registros..." />
            </div>
          )}

          <AgGridReact
            rowModelType="infinite"
            pagination={true}
            paginationPageSize={pageSize}
            paginationPageSizeSelector={[10, 25, 50]}
            cacheBlockSize={pageSize}
            maxBlocksInCache={5}
            components={components}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            onPaginationChanged={onPaginationChanged}
          />
        </div>
      )}

      {/* MODAL EDIT */}
      <ReusableModal
        isOpen={showEditModal}
        title="Editar Registro"
        onClose={() => setShowEditModal(false)}
      >
        <ParkingRegisterForm
          parkingData={selectedParking}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedParking(null);
            
            setConfirmModal({
              isOpen: true,
              type: "success",
              title: "Registro actualizado",
              message: "Los datos del registro se guardaron correctamente.",
              onConfirm: () =>
                setConfirmModal({ ...confirmModal, isOpen: false }),
            });
            if(gridApiRef?.current?.purgeInfiniteCache){
              setLoading(true);
              gridApiRef.current?.purgeInfiniteCache();
            }
            // MOBILE
            if(isMobile){
              loadMobileData();
            }
          }}
          onError={(error) => {
            const backendError =
              typeof error === "string"
                ? error
                : error?.response?.data?.error ||
                  error?.response?.data?.message ||
                  error?.message ||
                  "Error al editar el registro";

            const message = cleanBackendErrorMessage(backendError);

            setShowEditModal(false);
            setConfirmModal({
              isOpen: true,
              type: "error",
              title: "Error al editar el registro",
              message: message || backendError || "Error al editar el registro",
              onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
            });
          }}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedParking(null);
          }}
        />
      </ReusableModal>

      <ReusableModal
        isOpen={exportModal.isOpen}
        title="Exportar Excel"
        onClose={() => setExportModal({ isOpen: false, fecha_inicial: "", fecha_final: "" })}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha inicial
            </label>
            <input
              type="date"
              value={exportModal.fecha_inicial}
              onChange={(e) =>
                setExportModal((prev) => ({ ...prev, fecha_inicial: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha final
            </label>
            <input
              type="date"
              value={exportModal.fecha_final}
              onChange={(e) =>
                setExportModal((prev) => ({ ...prev, fecha_final: e.target.value }))
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <CancelButton
              onClick={() => setExportModal({ isOpen: false, fecha_inicial: "", fecha_final: "" })}
            />
            <button
              type="button"
              onClick={handleExportParkingExcel}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500"
            >
              Generar
            </button>
          </div>
        </div>
      </ReusableModal>

      {/* MODAL CONFIRMAR */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </>
  );
};

export default ParkingRegister;
